// ---------------------------------------------------------------------------
// THE TONE OF A STATUS, as one colour per status per mode.
//
// THE COLOURS FOLLOW THE PORTAL'S STATUSES AS THEY ARE SHOWN TODAY. The owner
// looked at the pilot beside a production list (07/10/2026 afternoon: "i
// colori non seguono ancora quelli degli status che vedi"): Ready 2 Allocate
// is the info blue, Formatting in Progress the secondary blue-indigo, Claimed
// red, Linguist Delivery green, Allocated amber, To Be Claimed grey. So a
// registry key resolves to the THEME'S OWN RAMP for that key — the same ramp
// every other list, pill and tile in the portal reads — and not to the five
// target tones of DESIGN.md §4, which collapsed the three blues onto the
// accent. Those five stay below as TARGET_TONES, adopted only when DECISION 4
// is taken and the owner has seen them.
//
// ONE FACE FOR THE MARK AND THE ICON (owner, 07/10 morning: "icons need to
// have the same colour as the images in the header"): the 8px dot beside a
// status word, the rungs of a step bar and the stage icon of the pipeline
// strip are all drawn in the tone's INK — the ramp's dark step on light
// paper, its main step on dark paper — which is also what `utils/tones.js`
// gives every production list for a toned figure. `mark` (the ramp's main
// step) is the base of the tint only, exactly as the production pill tints
// are made.
//
// `default` and `neutral` are grey: the secondary text ink, filled (the
// production To Be Claimed dot is a filled grey dot, not a ring). `accent`
// is the pilot's accent. An unknown key is neutral, never an error.
// ---------------------------------------------------------------------------

import { alpha, getContrastRatio } from '@mui/material/styles';
import { accentOf, controlLineOf } from './brand';

/** DESIGN.md §4's five tones: a target, not yet what the owner has approved. */
export const TARGET_TONES = Object.freeze({
  neutral: { mark: null, ink: { light: '#5B6778', dark: '#A9B4C6' } },
  progress: { mark: '#7C5CE6', ink: { light: '#5B46A8', dark: '#B3A1F5' } },
  success: { mark: '#22A55A', ink: { light: '#1A7342', dark: '#4FCB7F' } },
  warning: { mark: '#BF7F08', ink: { light: '#9A5B00', dark: '#F2B544' } },
  error: { mark: '#E0463B', ink: { light: '#B9302A', dark: '#F28B82' } }
});

// The registry keys the workflows and the allocation statuses emit, and the
// theme ramp each one reads. `progress` is the one §4 key with no ramp of its
// own yet: until DECISION 4 it reads the purple ramp.
const RAMPS = Object.freeze({
  info: 'info',
  secondary: 'secondary',
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  error: 'error',
  purple: 'purple',
  progress: 'purple',
  pink: 'pink',
  blue: 'blue',
  cyan: 'cyan'
});

const isLight = (theme) => theme.palette.mode === 'light';

// Tones the theme has no ramp for, spelled here: `soon` is the orange of a
// deadline that falls tomorrow (the owner, 08/10/2026), between the red of
// today and the amber of the days after.
const CUSTOM = Object.freeze({
  soon: { mark: '#F97316', light: '#C2410C', dark: '#FDBA74' },
  // Client deadlines by urgency (the owner, 21/10/2026): late purple, today
  // red, tomorrow orange, the day after yellow. `mark` fills the countdown
  // pill (toneFillOf); light/dark are the inks of the text.
  late: { mark: '#9333EA', light: '#7E22CE', dark: '#C084FC' },
  today: { mark: '#DC2626', light: '#B91C1C', dark: '#F87171' },
  // `on`: the word on the fill, white as on today's red (the owner, 22/10/2026)
  dueTomorrow: { mark: '#F97316', light: '#C2410C', dark: '#FB923C', on: '#FFFFFF' },
  dueSoon: { mark: '#FACC15', light: '#A16207', dark: '#FACC15' },
  // THE BALL IS IN OUR COURT (report "Allocations UI", 15/10/2026): a
  // linguist's delivery waiting for the PM's review wore the same green as
  // "Delivered", and the amber of an offer nobody has answered yet is a
  // different wait — on them, not on us. A burnt orange of its own: the
  // ink the report proposed (#8A3B00, 7,9:1 on its tint), a peach tint
  // from the orange-600 mark, and a lighter orange ink on the night.
  review: { mark: '#EA580C', light: '#8A3B00', dark: '#FDBA74' }
});

const rampOf = (theme, key) => {
  const name = RAMPS[key];
  const ramp = name && theme.palette[name];
  return ramp && ramp.main ? ramp : null;
};

/** What a registry key names: 'neutral', 'accent', or a theme ramp key. */
export const toneKey = (key) => {
  const name = String(key || '').trim();
  if (!name || name === 'default' || name === 'neutral') return 'neutral';
  if (name === 'accent') return 'accent';
  if (CUSTOM[name]) return name;
  return RAMPS[name] ? name : 'neutral';
};

/**
 * The ink of a tone: text, the status mark, a stage icon, a toned figure.
 * The ramp's dark step on light paper, its main step on dark paper.
 */
export const toneInkOf = (theme, key) => {
  const tone = toneKey(key);
  if (tone === 'neutral') return theme.palette.text.secondary;
  if (tone === 'accent') return accentOf(theme);
  if (CUSTOM[tone])
    return isLight(theme) ? CUSTOM[tone].light : CUSTOM[tone].dark;
  const ramp = rampOf(theme, tone);
  if (!ramp) return theme.palette.text.secondary;
  return isLight(theme) ? ramp.dark || ramp.main : ramp.main;
};

/**
 * The fill of a status mark or a rung: the same ink, so it matches the stage
 * icon above it. Neutral is a filled grey dot.
 */
export const toneMarkOf = (theme, key) => toneInkOf(theme, key);

/** The solid fill of a custom tone (its bright `mark`), else its ink. */
export const toneFillOf = (theme, key) => CUSTOM[toneKey(key)]?.mark || toneInkOf(theme, key);

/**
 * The soft tint a lead pill or a lit strip cell sits on, made the way the
 * portal's ghost pills are: the ramp's main step at .16 on light paper,
 * .22 on dark; a step more under the pointer.
 */
export const toneTintOf = (theme, key, step = 'rest') => {
  const tone = toneKey(key);
  const base =
    tone === 'neutral'
      ? theme.palette.text.secondary
      : tone === 'accent'
      ? accentOf(theme)
      : CUSTOM[tone]
      ? CUSTOM[tone].mark
      : (rampOf(theme, tone) || {}).main || theme.palette.text.secondary;
  const light = isLight(theme);
  const by = {
    rest: light ? 0.16 : 0.22,
    hover: light ? 0.24 : 0.3,
    // the status label (§19): a lighter wash than a lit cell, and the thin
    // line that keeps its edge crisp — a label is read, not pressed
    label: light ? 0.1 : 0.16,
    line: light ? 0.28 : 0.4
  };
  return alpha(base, by[step] ?? by.rest);
};

/** The ring of a mark that has no fill (kept for callers that draw one). */
export const toneRingOf = (theme) => controlLineOf(theme);

// ---------------------------------------------------------------------------
// CATEGORICAL MARKS (DESIGN.md §4): facts a row carries that are categories,
// not states — equipment on an event, an AI interpreting session, a project
// nobody owns yet — drawn as marks in their own inks so the glyphs before and
// after a project's name read apart from one another (the owner, 07/10/2026:
// "the icons next to the project number can have other colors", meaning the
// attention glyphs). Teal for the hardware; for the AI the pink Aura — the
// AI interpreting product — already wears in the portal (brochureTokens);
// the §4 chart violet for the project without an owner. Inks only: a
// categorical colour never fills a row, a card or a button.
// ---------------------------------------------------------------------------
export const CATEGORICAL = Object.freeze({
  equipment: { light: '#0F766E', dark: '#5EEAD4' },
  ai: { light: '#DB2777', dark: '#F472B6' },
  unowned: { light: '#5B46A8', dark: '#B3A1F5' },
  // a sequence listing on a patent project: a document that travels with
  // the files, the same teal as the hardware mark of an event
  sequence: { light: '#0F766E', dark: '#5EEAD4' },
  // what a translation carries besides the translation (the translation list,
  // 30/10/2026). Momen, the same afternoon: "il camion farlo color amazon, il
  // certificato farlo di un altro colore". The shipping truck in Amazon's
  // orange — #FF9900 on the night, Amazon's deeper #E47911 on paper, where the
  // bright one would not hold 3:1 for a glyph — and the certificate's seal in
  // the teal of the things a project carries (the hardware of an event, the
  // sequence listing of a patent), nothing a state of this list owns
  certified: { light: '#0F766E', dark: '#5EEAD4' },
  shipping: { light: '#E47911', dark: '#FF9900' },
  // the golden pin's own metal: the mark a person who HOLDS one wears beside
  // their name on the Quality list. The same two steps `goldFor` has given
  // the golden-pin pages since they were written, moved here because a
  // categorical colour belongs in the registry (§4) and not in a section's
  // tokens; an ink only, never a fill.
  golden: { light: '#C99A06', dark: '#F2C14E' }
});

/** The ink of a categorical mark; an unknown key falls back to the secondary ink. */
export const categoricalInkOf = (theme, key) => {
  const entry = CATEGORICAL[key];
  if (!entry) return theme.palette.text.secondary;
  return isLight(theme) ? entry.light : entry.dark;
};

/**
 * THE WASH AND THE LINE OF A LABEL DRAWN IN AN INK OF ITS OWN — the steps a
 * status label wears (`toneTintOf`), for the vocabularies whose colour is a
 * category rather than a tone: a department, a brand, a cost family. One
 * place, so a label is the same object in every vocabulary and the four
 * registries cannot drift a percentage point apart.
 */
export const inkTintOf = (theme, ink, step = 'label') => {
  const light = isLight(theme);
  const by = {
    rest: light ? 0.16 : 0.22,
    label: light ? 0.1 : 0.16,
    line: light ? 0.28 : 0.4,
    hover: light ? 0.18 : 0.26
  };
  return alpha(ink || theme.palette.text.secondary, by[step] ?? by.label);
};

/**
 * THE WORD ON A FILLED LABEL, when the fill is a categorical ink. A tone's
 * filled label is always white on the tone's ink, which works because a tone
 * ink is dark on paper — but a categorical ink is LIGHT on the night (that is
 * what makes it readable there), and white on #A5B4FC is 1.9:1. So the word
 * is whichever of the two house inks the fill can actually carry.
 */
export const onInkOf = (theme, ink) => {
  const white = theme.palette.common.white;
  const dark = theme.palette.grey[800];
  if (!ink) return white;
  return getContrastRatio(ink, white) >= getContrastRatio(ink, dark)
    ? white
    : dark;
};

/** The word on a tone's solid fill (toneFillOf): the tone's own `on`, else the house ink the fill carries. */
export const toneOnFillOf = (theme, key) =>
  CUSTOM[toneKey(key)]?.on || onInkOf(theme, toneFillOf(theme, key));

// ---------------------------------------------------------------------------
// THE DEPARTMENTS, as inks (DESIGN.md §4 "Categorical colour"). A department
// is a category, not a state: the desk a piece of work falls on. It gets a
// colour for the same reason an owner does — so the eye finds the desk by its
// hue before it reads the word — and it wears that colour on EVERY page the
// department is named on, which is what `DepartmentLabel` is for.
//
// Nine keys, not nine names: the portal stores the same desk under several
// spellings ("Human Resources" on a quality case, "Human Resource Management"
// on a task board, "HR" on a bit), and the label resolves all of them to one
// key here, while always rendering the stored word verbatim.
//
// MEASURED on 08/10/2026 with the script beside this file's history — the word
// in its ink on a 10% (light) / 16% (dark) wash of the SAME ink, over paper,
// the canvas and the sunken well:
//
//   worst of the eighteen readings 5.07:1 (the board, dark) — every other
//   ≥ 5.26; the floor for text is 4.5 (§24).
//
// Told apart from one another, and from the state tones, by ΔE76: the closest
// pair is finance / support 13.4 (light), 14.0 (dark), and the closest state
// ink is the progress indigo at 14.8 from `projects`. A label is never colour
// alone — it carries the stage's glyph and the stored word beside it (§4).
// ---------------------------------------------------------------------------
export const DEPARTMENT_INKS = Object.freeze({
  projects: { light: '#3730A3', dark: '#A5B4FC' }, // indigo — the board work moves across
  accounts: { light: '#155E75', dark: '#67E8F9' }, // cyan — the client side
  marketing: { light: '#9A3412', dark: '#FDBA74' }, // orange — the loudspeaker
  finance: { light: '#065F46', dark: '#6EE7B7' }, // emerald — the money
  people: { light: '#86198F', dark: '#F0ABFC' }, // fuchsia — the people records
  development: { light: '#3F6212', dark: '#A3E635' }, // lime — the code
  growth: { light: '#9D174D', dark: '#FDA4AF' }, // pink — new business
  support: { light: '#115E59', dark: '#5EEAD4' }, // teal — the equipment desk
  board: { light: '#334155', dark: '#94A3B8' } // slate — the house, not a trade
});

/** The ink of a department; a desk with no key falls back to the secondary. */
export const departmentInkOf = (theme, key) => {
  const entry = DEPARTMENT_INKS[key];
  if (!entry) return theme.palette.text.secondary;
  return isLight(theme) ? entry.light : entry.dark;
};

/**
 * The wash and the line of a department label — the same steps the status
 * label wears (`toneTintOf`), so the two labels are one object in two
 * vocabularies.
 */
export const departmentTintOf = (theme, key, step = 'label') =>
  inkTintOf(theme, departmentInkOf(theme, key), step);

// ---------------------------------------------------------------------------
// THE FIVE BRANDS OF THE GROUP, as inks (DESIGN.md §4 "Categorical colour").
// Which company took the order is a CATEGORY, not a state, and it wears the
// same label geometry a status, an owner and a department wear — in the
// brand's own hue instead of a tone's.
//
// The hues are the group's own, from the Guidelines manual, and they live in
// one place (`ORDER_BRANDS` in the orders list's tokens). What is here is the
// LIGHTNESS each one has to be taken to in order to be read: #65C4E0 as a
// word on its own 10% wash reads 4.24:1 on paper and #784593 reads 3.51:1 on
// the night — both under the 4.5 floor of §24 — so the ink of a brand is its
// hue at the lightness where it clears it, the way a department's is.
//
// MEASURED on 09/10/2026 with the script beside this file's history — the
// word in its ink on a 10% (light) / 16% (dark) wash of the SAME ink, over
// paper (#FFFFFF), the canvas (#F6F5FA) and the sunken well (#EDF1F7) on
// paper, and over paper (#212B36) and the well (#0D172E) on the night:
//
//   every reading 4.70:1 or better; the worst is LingoYou Translate on the
//   canvas at 4.70 and Barzanò on the night at 4.71.
//
// Told apart from one another by ΔE76: the closest pair is Translate /
// Congress, 16.9 light and 13.1 dark — the same order as the departments'
// closest pair (13.4 / 14.0) — and the nearest state ink is the neutral at
// 14.3 from Mediation on the night. A brand label is never colour alone: it
// carries the brand's own word beside the hue, and the project reference in
// the row before it already begins with the brand's code.
//
// MEDIATION IS NOT LILAC ANY MORE (DECISION 12). The manual gives Translate
// Celestial Blue and Congress Aero; the other three brands have no chapter
// of their own and carried placeholders, and two of those placeholders were
// both purple — taken to a readable lightness, the mediation lilac (#C1A8D1)
// and Barzanò's Cadmium Violet (#784593) land 8.0 ΔE apart on paper and 5.1
// on the night, which is one colour wearing two names on a list that exists
// to tell the five brands apart. Mediation takes the midpoint of the Group
// gradient instead (#606FB3, the indigo already named in `layouts/auth/
// portals.js` — "not a new hue"), which is 19.5 / 15.3 from Translate and
// 24.3 / 15.7 from Barzanò. It stays a placeholder: when the brand team
// gives Mediation a colour of its own, this row and `ORDER_BRANDS` change.
// ---------------------------------------------------------------------------
export const BRAND_INKS = Object.freeze({
  LY: { light: '#246797', dark: '#79B5DF' }, // Celestial Blue — official
  CI: { light: '#1A6A81', dark: '#4BBADB' }, // Aero — official
  'CI-MC': { light: '#4E5DA2', dark: '#A4ACD3' }, // the gradient's indigo
  'LY-BZ': { light: '#814A9E', dark: '#C2A1D3' }, // Cadmium Violet
  'LY-BG': { light: '#9E480D', dark: '#F19555' } // Vivid Tangelo
});

/** The ink of a brand, by the code its project numbers begin with. */
export const brandInkOf = (theme, type) => {
  const entry = BRAND_INKS[type];
  if (!entry) return theme.palette.text.secondary;
  return isLight(theme) ? entry.light : entry.dark;
};

/** The wash and the line of a brand label — the status label's own steps. */
export const brandTintOf = (theme, type, step = 'label') =>
  inkTintOf(theme, brandInkOf(theme, type), step);

// ---------------------------------------------------------------------------
// THE COST FAMILIES OF THE EXPENSES LEDGER, as inks (DESIGN.md §4
// "Categorical colour"). What a cost WAS is a category, not a state, and the
// owner asked for it in colour (09/10/2026: "dai colori alle label
// categorie") — so the twenty macro categories the books keep are drawn in
// SEVEN inks, one per family of cost, and the neutral for the catch-all.
//
// SEVEN, NOT TWENTY. The September list painted twelve hues straight onto the
// stored words and the migration took them off as ornament; what was wrong
// there was not colour but colour WITHOUT A VOCABULARY — twelve hues nobody
// could learn, two of them the same green. A family is learnable: the hue
// says what KIND of money it was (the people who did the work, our own
// payroll, the road, the tools, the loudspeaker, the code, the house), and
// the stored word beside it — always rendered verbatim — says which of the
// twenty it is. Measured on the live books (09/10/2026, 11.754 expenses): one
// screen of 50 rows carries thirteen different macros, so a column of one
// grey was genuinely asking to be read word by word.
//
// THE BRANDS KEEP THE BLUES. The PO beside this column wears the brand's own
// hue (`BRAND_INKS`) and the three brands that actually pay for expenses are
// Translate steel blue, Congress aero and Barzanò violet — 5.403, 4.201 and
// 5.093 of the PO entries, against 33 and 27 for the other two. So the cost
// families take the wheel those three leave: indigo, fuchsia, pink,
// terracotta, teal, lime, green. Red and amber are not here at all — on this
// page they mean LATE and OWED (the payment column and the Due reading), and
// a category must never borrow a state's colour.
//
// MEASURED on 09/10/2026 with the script beside this file's history — the
// word in its ink on a 10% (light) / 16% (dark) wash of the SAME ink, over
// paper (#FFFFFF), the canvas (#F6F5FA) and the sunken well (#EDF1F7) on
// paper, and over paper (#212B36) and the well (#0D172E) on the night:
//
//   every reading 5.15:1 or better; the worst is the work indigo on the
//   night at 5.15 and the road on the night at 5.37. The floor is 4.5 (§24).
//
// Told apart from one another by ΔE76: the closest pair is code / house 19.6
// on paper and tools / house 23.9 on the night — half again the departments'
// closest pair (13.4 / 14.0). The nearest ink from another vocabulary is the
// road against Bugnion's tangelo (11.0 / 13.1) and against the `soon` orange
// of a deadline that falls tomorrow (17.6 / 11.0); the first is a FILLED chip
// in the column alongside on 33 rows of 11.754, the second lives in the Due
// column, and both carry a word of their own.
// ---------------------------------------------------------------------------
export const MACRO_INKS = Object.freeze({
  // the people who do the billable work — freelancers, and the Academy's
  // teachers. The biggest family of all: 3.820 rows, €1,92M
  work: { light: '#3730A3', dark: '#A5B4FC' }, // indigo
  staff: { light: '#86198F', dark: '#F0ABFC' }, // fuchsia — our own payroll
  marketing: { light: '#9D174D', dark: '#FDA4AF' }, // pink — the loudspeaker
  // the road: tolls, hire, fuel, and the hotels and meals of the people sent
  // out. Four macros, 2.888 rows — one bucket on the books and in the head
  travel: { light: '#9A3412', dark: '#F7A977' }, // terracotta
  // what we buy to work with: the licences, the hosting, the line — and the
  // booths, headsets and microphones. The portal's own equipment teal
  tools: { light: '#115E59', dark: '#5EEAD4' },
  code: { light: '#3F6212', dark: '#A3E635' }, // lime — the dev team
  // the house's own costs: the office and its loan, the taxes, the bank, the
  // insurances, the accountant, and the duties and postage a sale carries
  house: { light: '#166534', dark: '#86EFAC' } // green
});

/**
 * The ink of a cost family. A family the registry has never seen — and "AOB",
 * which is the books' own word for "no family" — falls back to the secondary
 * ink, the neutral every other categorical vocabulary falls back to.
 */
export const macroInkOf = (theme, key) => {
  const entry = MACRO_INKS[key];
  if (!entry) return theme.palette.text.secondary;
  return isLight(theme) ? entry.light : entry.dark;
};

/** The wash and the line of a cost-family label — the label's own steps. */
export const macroTintOf = (theme, key, step = 'label') =>
  inkTintOf(theme, macroInkOf(theme, key), step);
